import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";

import {
	apiPaths,
	mfaSecretResponseSchema,
	profileMfaSchema,
	signedInResponseSchema,
	type AnswerMfaChallengeRequest,
	type MfaSecretResponse,
	type SignedInResponse,
	type StartMfaSetupRequest,
	type VerifyMfaCodeRequest,
	type VerifyMfaSetupRequest,
} from "@ravnar/api-contract";

import { apiRequest } from "./client";

// Two-step verification's requests. The query key starts with "settings", so a save refreshes every settings query.

/** Whether the signed-in member signs in with an authenticator app. */
export const profileMfaQueryOptions = () =>
	queryOptions({
		queryKey: ["settings", "mfa"],
		queryFn: ({ signal }) => apiRequest(apiPaths.profileMfa, profileMfaSchema, { signal }),
	});

/** Starts setting up an authenticator app: the API hands out a new shared secret, unconfirmed until verifyMfaSetup. */
export function startMfaSetup(): Promise<MfaSecretResponse> {
	return apiRequest(apiPaths.profileMfaSetup, mfaSecretResponseSchema, { method: "POST" });
}

/** Confirms the setup with one code from the app and turns two-step verification on. Fails with 400 for a wrong code. */
export async function verifyMfaSetup(request: VerifyMfaCodeRequest): Promise<void> {
	await apiRequest(apiPaths.profileMfaVerify, z.undefined(), { method: "POST", body: request });
}

/** Turns two-step verification off for the signed-in member. */
export async function disableMfa(): Promise<void> {
	await apiRequest(apiPaths.profileMfa, z.undefined(), { method: "DELETE" });
}

/**
 * Answers the authenticator-app challenge a sign-in or a password reset raised and opens the session. Fails with 400
 * for a wrong code and 401 once the 3 minutes to answer have passed.
 */
export function answerMfaChallenge(request: AnswerMfaChallengeRequest): Promise<SignedInResponse> {
	return apiRequest(apiPaths.sessionMfa, signedInResponseSchema, { method: "POST", body: request });
}

/**
 * Starts setting up an authenticator app for a member whose organization requires one and who was just refused a
 * session for having none, with the same email and password they just signed in with. Fails with 401 for a wrong
 * password and 409 when the member already has a factor.
 */
export function startSignInMfaSetup(request: StartMfaSetupRequest): Promise<MfaSecretResponse> {
	return apiRequest(apiPaths.sessionMfaSetup, mfaSecretResponseSchema, { method: "POST", body: request });
}

/** Confirms that setup with one code from the app and opens the session. Fails with 400 for a wrong code. */
export function verifySignInMfaSetup(request: VerifyMfaSetupRequest): Promise<SignedInResponse> {
	return apiRequest(apiPaths.sessionMfaVerify, signedInResponseSchema, { method: "POST", body: request });
}
